import type { IGatsbyImageData } from "gatsby-plugin-image"
import UniversalImage from "library/UniversalImage"
import { fmobile, fresponsive } from "library/fullyResponsive"
import styled, { css } from "styled-components"
import colors from "styles/colors"
import textStyles from "styles/text"

interface Image {
	childImageSharp: {
		gatsbyImageData: IGatsbyImageData | null
	} | null
}

interface Item {
	company: string | null
	logo: Image | null
	photo: Image | null
	review: string | null
	name: string | null
	role: string | null
}

export default function Card({
	backgroundColor,
	item: { company, logo, name, photo, review, role },
}: {
	backgroundColor: string
	item: Item
}) {
	return (
		<Wrapper $backgroundColor={backgroundColor}>
			<Logo
				image={logo?.childImageSharp?.gatsbyImageData}
				alt={`${company} logo`}
			/>
			<Photo
				image={photo?.childImageSharp?.gatsbyImageData}
				alt={`Photo of ${name}`}
			/>
			<Details>
				<Review>&ldquo;{review}&rdquo;</Review>
				<Name>{name}</Name>
				<Role>{role}</Role>
			</Details>
		</Wrapper>
	)
}

const border = css`
	content: "";
	background: transparent;
	width: 25px;
	height: 25px;
	position: absolute;
	z-index: 3;
	border-top: 10px solid var(--background);
	border-left: 10px solid var(--background);
	border-top-left-radius: 100%;
`

const Wrapper = styled.div<{
	$backgroundColor: string
}>`
	position: relative;
	--background: ${({ $backgroundColor }) => $backgroundColor};

	${fresponsive(css`
		border-radius: 0 15px 15px;
		overflow: clip;
		isolation: isolate;
		width: 322px;
		display: grid;
		grid-template-rows: auto 1fr;

		&::before {
			${border}
			top: -10px;
			left: 70px;
		}

		&::after {
			${border}
			left: -10px;
			top: 70px;
		}
	`)}

	${fmobile(css`
		width: 242px;

		&::before {
			left: 40px;
		}

		&::after {
			top: 40px;
		}
	`)}
`

const Logo = styled(UniversalImage)`
	border: 1px solid ${colors.blue3};

	${fresponsive(css`
		position: absolute;
		top: 0;
		left: 0;
		height: 70px;
		width: 70px;
		border-radius: 8px;
		z-index: 2;
		outline: 10px solid var(--background);
	`)}

	${fmobile(css`
		height: 40px;
		width: 40px;
	`)}
`

const Photo = styled(UniversalImage)`
	width: 100%;
	margin-bottom: -1px;
	${fresponsive(css`
		height: 310px;
	`)}
	${fmobile(css`
		height: 232px;
	`)}
`

const Details = styled.div`
	${fresponsive(css`
		background: ${colors.blue2};
		color: white;
		padding: 20px;
		display: flex;
		flex-direction: column;
	`)}

	${fmobile(css`
		padding: 15px;
	`)}
`

const Review = styled.div`
	${textStyles.p2};
	margin-bottom: auto;
	${fmobile(css`
		${textStyles.p3};
	`)}
`

const Name = styled.div`
	${textStyles.sub1}
	border-top: 1px solid white;
	${fresponsive(css`
		margin-bottom: 10px;
		padding-top: 20px;
		margin-top: 20px;
	`)}
	${fmobile(css`
		${textStyles.sub2};
		padding-top: 15px;
		margin-top: 15px;
	`)}
`

const Role = styled.div`
	${textStyles.sub3};
	color: ${colors.blue4};
`
